import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, subscriptionAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useGoogleLogin } from '@react-oauth/google';
import { PublicClientApplication } from '@azure/msal-browser';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import './AuthPages.css';

interface LoginMethods {
  otp: boolean;
  google: boolean;
  microsoft: boolean;
  google_client_id: string;
  microsoft_client_id: string;
  microsoft_tenant_id: string;
}

const ModernLoginPage: React.FC = () => {
  useDocumentTitle('Login');
  const [searchParams] = useSearchParams();
  // Only same-site paths ("/x"), never "//evil.com" or absolute URLs.
  const requested = searchParams.get('redirect') || '';
  const redirectTo = /^\/(?!\/)/.test(requested) && !requested.startsWith('/login') ? requested : '/dashboard';
  const prefillEmail = searchParams.get('email') || '';

  const [email, setEmail] = useState(prefillEmail);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [emailError, setEmailError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const [loginMethods, setLoginMethods] = useState<LoginMethods | null>(null);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const { login, setSubscriptionStatus } = useAuth();

  // Fetch login methods on mount
  useEffect(() => {
    authAPI.getLoginMethods()
      .then((res) => setLoginMethods(res.data))
      .catch(() => setLoginMethods({ otp: true, google: false, microsoft: false, google_client_id: '', microsoft_client_id: '', microsoft_tenant_id: '' }));
  }, []);

  // Check subscription status after login and redirect if payment pending
  const checkSubscriptionAndNavigate = useCallback(async (destination: string) => {
    try {
      const subRes = await subscriptionAPI.getCurrent();
      const orgStatus = subRes.data.data?.org_status;
      const pendingSub = subRes.data.data?.pending_subscription;
      setSubscriptionStatus(orgStatus || null, pendingSub?.plan_id ?? null, pendingSub?.billing_period ?? null);
      if (orgStatus === 'pending_payment' && pendingSub) {
        navigate(`/billing/checkout?plan=${pendingSub.plan_id}&period=${pendingSub.billing_period}`);
        return;
      }
    } catch {
      // Ignore subscription check errors - proceed with normal navigation
    }
    navigate(destination);
  }, [navigate, setSubscriptionStatus]);

  // Handle OAuth response (shared between Google and Microsoft)
  const handleOAuthResponse = useCallback(async (data: any) => {
    if (data.action === 'login') {
      login(data.user, data.token);
      toast.success('Login successful!');
      await checkSubscriptionAndNavigate(redirectTo);
    } else if (data.action === 'register') {
      // New user — store info and redirect to signup step 2
      sessionStorage.setItem('oauth_verified', 'true');
      sessionStorage.setItem('oauth_email', data.email);
      sessionStorage.setItem('oauth_name', data.name || '');
      toast.success('Email verified! Please complete your registration.');
      navigate(`/signup?email=${encodeURIComponent(data.email)}`);
    }
  }, [login, navigate, redirectTo, checkSubscriptionAndNavigate]);

  // Google login handler
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setOauthLoading('google');
      try {
        // Exchange the access token for an id_token via Google's tokeninfo
        // useGoogleLogin with flow='implicit' gives access_token; we need id_token
        // Instead, we'll use the auth-code flow or get userinfo
        // Actually, let's use the Google userinfo endpoint to get user details,
        // then send the access_token to our backend which will verify it
        const res = await authAPI.googleOAuth(tokenResponse.access_token);
        if (res.data.status === 'success') {
          await handleOAuthResponse(res.data);
        } else {
          toast.error(res.data.message || 'Google login failed');
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Google login failed');
      } finally {
        setOauthLoading(null);
      }
    },
    onError: () => {
      toast.error('Google login was cancelled');
      setOauthLoading(null);
    },
    flow: 'implicit',
  });

  // Microsoft login handler
  const handleMicrosoftLogin = async () => {
    if (!loginMethods?.microsoft_client_id) return;
    setOauthLoading('microsoft');
    try {
      const msalInstance = new PublicClientApplication({
        auth: {
          clientId: loginMethods.microsoft_client_id,
          authority: `https://login.microsoftonline.com/${loginMethods.microsoft_tenant_id || 'common'}`,
          redirectUri: window.location.origin,
        },
      });
      await msalInstance.initialize();
      const result = await msalInstance.loginPopup({
        scopes: ['User.Read'],
      });

      const res = await authAPI.microsoftOAuth(result.accessToken);
      if (res.data.status === 'success') {
        await handleOAuthResponse(res.data);
      } else {
        toast.error(res.data.message || 'Microsoft login failed');
      }
    } catch (error: any) {
      if (error.errorCode !== 'user_cancelled') {
        toast.error(error.response?.data?.message || 'Microsoft login failed');
      }
    } finally {
      setOauthLoading(null);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setEmailError('');
    setShowRegisterPrompt(false);

    try {
      const response = await authAPI.sendLoginOTP(email);

      if (response.data.status === 'error') {
        if (response.data.redirect_to_signup) {
          setShowRegisterPrompt(true);
        } else {
          setEmailError(response.data.message);
        }
        return;
      }

      setOtpSent(true);
      toast.success('OTP sent to your email!');
    } catch (error: any) {
      setEmailError(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setOtpError('');

    try {
      const response = await authAPI.verifyLoginOTP(email, otp, rememberMe);

      if (response.data.status === 'error') {
        setOtpError(response.data.message);
        return;
      }

      const { token, user } = response.data;
      login(user, token);

      toast.success('Login successful!');
      await checkSubscriptionAndNavigate(redirectTo);
    } catch (error: any) {
      setOtpError(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Left Side - Form */}
        <div className="auth-form-section">
          <div className="auth-form-wrapper">
            {/* Logo */}
            <Link to="/" className="auth-logo">
              <div className="logo-icon">
                <span className="logo-text">C</span>
              </div>
              <span className="brand-name">CloudAyn</span>
            </Link>

            {/* Form Header */}
            <div className="auth-header">
              <h1>Welcome back</h1>
              {redirectTo.startsWith('/invitation/') ? (
                <p>Sign in to accept your invitation and join your team.</p>
              ) : (
                <p>Login to access your dashboard and monitor your assets</p>
              )}
            </div>
            {redirectTo.startsWith('/invitation/') && prefillEmail && (
              <div className="alert alert-info py-2 mb-3" style={{ fontSize: '0.875rem' }}>
                <i className="bi bi-envelope me-2"></i>
                Sign in with <strong>{prefillEmail}</strong> to accept the invitation.
              </div>
            )}

            {/* Login Form */}
            {!otpSent ? (
              <form onSubmit={handleSendOTP} className="auth-form">
                <div className="form-group">
                  <label>Email Address</label>
                  <div className="input-with-icon">
                    <i className="bi bi-envelope"></i>
                    <input
                      type="email"
                      className={`form-control ${emailError || showRegisterPrompt ? 'is-invalid' : ''}`}
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError('');
                        setShowRegisterPrompt(false);
                      }}
                      required
                    />
                  </div>
                  {emailError && !showRegisterPrompt && (
                    <div className="text-danger small mt-2">
                      <i className="bi bi-exclamation-circle me-1"></i>
                      {emailError}
                    </div>
                  )}
                  {showRegisterPrompt && (
                    <div className="mt-3 p-3 rounded" style={{
                      background: 'rgba(6, 182, 212, 0.05)',
                      border: '1px solid rgba(6, 182, 212, 0.2)'
                    }}>
                      <p className="text-muted small mb-2">
                        <i className="bi bi-info-circle me-1"></i>
                        No account found with this email. Please register first.
                      </p>
                      <Link
                        to={`/signup?email=${encodeURIComponent(email)}`}
                        className="btn btn-outline-primary btn-sm"
                      >
                        <i className="bi bi-person-plus me-1"></i>
                        Register Now
                      </Link>
                    </div>
                  )}
                </div>

                <button type="submit" className="btn btn-primary btn-lg w-100" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      Continue with Email
                      <i className="bi bi-arrow-right ms-2"></i>
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="auth-form">
                <div className="otp-sent-message">
                  <i className="bi bi-check-circle-fill"></i>
                  <p>We've sent a verification code to <strong>{email}</strong></p>
                </div>

                <div className="form-group">
                  <label>Verification Code</label>
                  <div className="input-with-icon">
                    <i className="bi bi-shield-lock"></i>
                    <input
                      type="text"
                      className={`form-control ${otpError ? 'is-invalid' : ''}`}
                      placeholder="Enter 6-digit code"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        setOtpError('');
                      }}
                      maxLength={6}
                      required
                    />
                  </div>
                  {otpError && (
                    <div className="text-danger small mt-2">
                      <i className="bi bi-exclamation-circle me-1"></i>
                      {otpError}
                    </div>
                  )}
                </div>

                {/* Remember Me */}
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                  />
                  <label className="form-check-label small text-muted" htmlFor="rememberMe">
                    Keep me logged in for 30 days
                  </label>
                </div>

                <button type="submit" className="btn btn-primary btn-lg w-100" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Verifying...
                    </>
                  ) : (
                    'Verify & Login'
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-link w-100"
                  onClick={() => {
                    setOtpSent(false);
                    setOtpError('');
                  }}
                >
                  Change email address
                </button>
              </form>
            )}

            {/* OAuth Buttons */}
            {loginMethods && (loginMethods.google || loginMethods.microsoft) && (
              <>
                <div className="auth-divider">
                  <span>or continue with</span>
                </div>

                <div className="oauth-buttons">
                  {loginMethods.google && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-lg w-100 d-flex align-items-center justify-content-center gap-2"
                      onClick={() => googleLogin()}
                      disabled={oauthLoading !== null}
                    >
                      {oauthLoading === 'google' ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      )}
                      Google
                    </button>
                  )}
                  {loginMethods.microsoft && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-lg w-100 d-flex align-items-center justify-content-center gap-2"
                      onClick={handleMicrosoftLogin}
                      disabled={oauthLoading !== null}
                    >
                      {oauthLoading === 'microsoft' ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 23 23">
                          <rect fill="#f35325" x="1" y="1" width="10" height="10"/>
                          <rect fill="#81bc06" x="12" y="1" width="10" height="10"/>
                          <rect fill="#05a6f0" x="1" y="12" width="10" height="10"/>
                          <rect fill="#ffba08" x="12" y="12" width="10" height="10"/>
                        </svg>
                      )}
                      Microsoft
                    </button>
                  )}
                </div>
              </>
            )}

            {/* Divider */}
            <div className="auth-divider">
              <span>or</span>
            </div>

            {/* Sign Up Link */}
            <div className="auth-footer">
              <p>Don't have an account?</p>
              <Link to="/signup" className="auth-link">
                Create free account
                <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>

            <div className="made-in-bh">
              Made with <span className="bh-heart">❤️</span> in Bahrain 🇧🇭
            </div>
          </div>
        </div>

        {/* Right Side - Visual */}
        <div className="auth-visual-section">
          <div className="auth-visual-content">
            <div className="visual-shapes">
              <div className="visual-shape shape-1"></div>
              <div className="visual-shape shape-2"></div>
              <div className="visual-shape shape-3"></div>
            </div>

            <div className="visual-text">
              <h2>Monitor Your Digital Assets</h2>
              <p>Track visitors, monitor uptime, and manage security all in one powerful platform.</p>

              <div className="feature-list">
                <div className="feature-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Real-time analytics</span>
                </div>
                <div className="feature-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>24/7 monitoring</span>
                </div>
                <div className="feature-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Security scanning</span>
                </div>
              </div>
            </div>

            <div className="visual-mockup">
              <div className="mockup-browser">
                <div className="browser-header">
                  <div className="browser-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                <div className="browser-content">
                  <div className="stat-card animate-slide-in">
                    <div className="stat-icon">📊</div>
                    <div className="stat-info">
                      <div className="stat-value">12.5K</div>
                      <div className="stat-label">Visitors</div>
                    </div>
                  </div>
                  <div className="stat-card animate-slide-in" style={{ animationDelay: '0.2s' }}>
                    <div className="stat-icon">⚡</div>
                    <div className="stat-info">
                      <div className="stat-value">99.9%</div>
                      <div className="stat-label">Uptime</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernLoginPage;
